/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import {
	Directive,
	type OnDestroy,
	type TemplateRef,
	ViewContainerRef,
	effect,
	inject,
	input
} from "@angular/core";
import { Subject, takeUntil, tap } from "rxjs";
import { GlobalTemplateService } from "./global-template.service";

@Directive({
	selector: "[cbGlobalTemplateHost]",
	standalone: false,
})
export class GlobalTemplateHostDirective implements OnDestroy {
	private readonly globalControlsService: GlobalTemplateService = inject(
		GlobalTemplateService,
	);
	private readonly vcr: ViewContainerRef = inject(ViewContainerRef);

	public readonly cbGlobalTemplateHost = input<string>();

	private readonly destroy$: Subject<void> = new Subject<void>();

	private controlsTemplateRef: TemplateRef<unknown> | null = null;

	constructor() {
		effect(() => {
			const value = this.cbGlobalTemplateHost();

			if (!value) return;

			this.globalControlsService.controlsSubject
				.pipe(
					takeUntil(this.destroy$),
					tap((controls) => {
						if (controls[value]) {
							this.setControlsTemplateRef(controls[value]);
						} else {
							this.vcr.clear();
						}
					}),
				)
				.subscribe();
		});
	}

	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	private setControlsTemplateRef(templateRef: TemplateRef<any>): void {
		this.controlsTemplateRef = templateRef;
		this.vcr.clear();
		if (this.controlsTemplateRef) {
			this.vcr.createEmbeddedView(this.controlsTemplateRef);
		}
	}

	ngOnDestroy(): void {
		this.vcr.clear();
		this.destroy$.next();
		this.destroy$.complete();
	}
}
